import { useHover } from "@redotech/react-util/hover";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import classNames from "classnames";
import { memo, useState } from "react";
import { useMatch } from "react-router-dom";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";
import { TextWeightValue } from "../../../../theme/typography";
import { OverflowTooltip } from "../../../../tooltip/overflow-tooltip";
import { RedoBadge } from "../../../badge/redo-badge";
import { RedoIcon } from "../../../icon/redo-icon";
import * as navigationCss from "../../redo-navigation.module.css";
import { formatNotificationCount } from "../../secondary/redo-secondary-nav-item";

export type RedoViewItemProps = {
  friendlyName: string;
  linkHref: string;
  dragHandleProps: object;
  clone?: boolean;
  notificationCount?: number;
  /**
   * When true, the notification badge will always use the black/outline style
   * regardless of the count (no red "attention" indicator).
   */
  muted?: boolean;
};

export const RedoViewItem = memo(function RedoViewItem({
  friendlyName,
  dragHandleProps,
  linkHref,
  clone,
  notificationCount,
  muted,
}: RedoViewItemProps) {
  const active = useMatch({ path: `${linkHref}/*`, caseSensitive: true });

  const color: "primary" | "secondary" = active ? "primary" : "secondary";
  const textWeight: TextWeightValue = active ? "semibold" : "regular";

  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
  const [componentRef, setComponentRef] = useState<HTMLDivElement | null>(null);

  const hovered = useHover(componentRef);

  return (
    <Flex
      as="a"
      className={classNames(
        navigationCss.navItem,
        !!active && navigationCss.active,
      )}
      data-testid={`secondary-nav-tab-${friendlyName.toLowerCase().replace(/\s+/g, "-")}`}
      justify="space-between"
      linkProps={{ to: linkHref }}
      px="sm"
      py="sm"
      radius="sm"
      ref={setComponentRef}
      w="full"
      {...dragHandleProps}
    >
      <OverflowTooltip
        hideOnHover
        overflowRef={overflowRef}
        tooltipProps={{ title: friendlyName, darkMode: true }}
        wrapperStyle={{
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Text
          fontSize="xs"
          fontWeight={textWeight}
          overflow="hidden"
          ref={setOverflowRef}
          textColor={color}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {friendlyName}
        </Text>
      </OverflowTooltip>
      <Flex
        align="center"
        className={classNames(
          hovered && navigationCss.show,
          navigationCss.dragHandle,
          clone && navigationCss.clone,
        )}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        <RedoIcon color="tertiary" Icon={DotsReorderIcon} size={16} />
      </Flex>
      {notificationCount !== undefined && (
        <Flex>
          <RedoBadge
            color={muted || notificationCount === 0 ? "black" : "error"}
            size="xs"
            text={formatNotificationCount(notificationCount)}
            type="alternate"
          />
        </Flex>
      )}
    </Flex>
  );
});
